import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Eye, CheckCircle, Camera, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import { exportToExcel, exportToPDF } from "@/lib/export";

const EXPORT_COLS = [
  { header: "Tanggal", accessor: (r) => r.tanggal },
  { header: "Tambak", accessor: (r) => r.tambak_nama },
  { header: "DOC", accessor: (r) => r.hari_ke },
  { header: "Pakan (kg)", accessor: (r) => r.pakan?.[0]?.jumlah_kg || "-" },
  { header: "pH", accessor: (r) => r.kondisi_air?.ph || "-" },
  { header: "Suhu", accessor: (r) => r.kondisi_air?.suhu || "-" },
  { header: "Pelapor", accessor: (r) => r.pelapor_nama },
  { header: "Status", accessor: (r) => r.status },
];

export default function LaporanProduksiPage() {
  const [laporanList, setLaporanList] = useState([]);
  const [siklusList, setSiklusList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [filterSiklus, setFilterSiklus] = useState("__all__");
  const [form, setForm] = useState({
    siklus_id: "", tambak_id: "", tanggal: "", hari_ke: 1,
    pakan: [{ jenis: "", jumlah_kg: 0, frekuensi: 3 }],
    probiotik: "", probiotik_jumlah: 0,
    kondisi_air: { ph: null, suhu: null, salinitas: null, do_level: null, kecerahan: null },
    sampling: { berat_rata_rata: null, sr_estimasi: null, populasi_estimasi: null },
    kematian: 0, keterangan: "", foto_base64: ""
  });

  const load = useCallback(async () => {
    try {
      const siklusParam = filterSiklus && filterSiklus !== "__all__" ? `&siklus_id=${filterSiklus}` : "";
      const [lap, sik] = await Promise.all([
        api.get(`/laporan?page=${page}&limit=15&search=${search}${siklusParam}`),
        api.get("/siklus?limit=500")
      ]);
      setLaporanList(lap.data.data);
      setTotal(lap.data.total);
      setSiklusList(sik.data.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search, filterSiklus]);

  useEffect(() => { load(); }, [load]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, foto_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const resetForm = () => setForm({
    siklus_id: "", tambak_id: "", tanggal: "", hari_ke: 1,
    pakan: [{ jenis: "", jumlah_kg: 0, frekuensi: 3 }],
    probiotik: "", probiotik_jumlah: 0,
    kondisi_air: { ph: null, suhu: null, salinitas: null, do_level: null, kecerahan: null },
    sampling: { berat_rata_rata: null, sr_estimasi: null, populasi_estimasi: null },
    kematian: 0, keterangan: "", foto_base64: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/laporan", form);
      toast.success("Laporan berhasil dibuat");
      setDialogOpen(false);
      resetForm();
      load();
    } catch (err) {
      if (err.offlineQueued) {
        toast.info("Laporan disimpan di antrian offline. Akan dikirim saat online.");
        setDialogOpen(false);
        resetForm();
      } else {
        toast.error("Gagal membuat laporan");
      }
    }
  };

  const handleVerify = async (id) => {
    try {
      await api.put(`/laporan/${id}/verify`);
      toast.success("Laporan terverifikasi");
      load();
    } catch { toast.error("Gagal memverifikasi"); }
  };

  return (
    <div data-testid="laporan-produksi-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Laporan Produksi Harian
          </h1>
          <p className="text-sm text-slate-500 mt-1">Monitoring harian tambak udang</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            onExcel={() => exportToExcel(laporanList, EXPORT_COLS, "laporan_produksi")}
            onPDF={() => exportToPDF(laporanList, EXPORT_COLS, "laporan_produksi", "Laporan Produksi Harian P3UW")}
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-laporan-btn" className="bg-[#B8860B] hover:bg-[#9A7209] text-white">
                <Plus size={18} className="mr-2" /> Buat Laporan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Work Sans" }}>Laporan Monitoring Harian</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Siklus</Label>
                    <Select value={form.siklus_id} onValueChange={(v) => {
                      const s = siklusList.find(x => x._id === v);
                      setForm({ ...form, siklus_id: v, tambak_id: s?.tambak_id || "" });
                    }}>
                      <SelectTrigger data-testid="select-siklus"><SelectValue placeholder="Pilih Siklus" /></SelectTrigger>
                      <SelectContent>
                        {siklusList.map(s => (
                          <SelectItem key={s._id} value={s._id}>{s.tambak_nama} - {s.tanggal_tebar}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal</Label>
                    <Input data-testid="input-tanggal" type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Hari ke (DOC)</Label>
                    <Input data-testid="input-hari-ke" type="number" min={1} value={form.hari_ke} onChange={e => setForm({ ...form, hari_ke: parseInt(e.target.value) || 1 })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kematian</Label>
                    <Input data-testid="input-kematian" type="number" min={0} value={form.kematian} onChange={e => setForm({ ...form, kematian: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Pakan</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="Jenis pakan" value={form.pakan[0]?.jenis} onChange={e => setForm({ ...form, pakan: [{ ...form.pakan[0], jenis: e.target.value }] })} />
                    <Input placeholder="Jumlah (kg)" type="number" step="0.1" value={form.pakan[0]?.jumlah_kg || ""} onChange={e => setForm({ ...form, pakan: [{ ...form.pakan[0], jumlah_kg: parseFloat(e.target.value) || 0 }] })} />
                    <Input placeholder="Frekuensi" type="number" value={form.pakan[0]?.frekuensi || ""} onChange={e => setForm({ ...form, pakan: [{ ...form.pakan[0], frekuensi: parseInt(e.target.value) || 0 }] })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Probiotik</Label>
                    <Input placeholder="Jenis" value={form.probiotik} onChange={e => setForm({ ...form, probiotik: e.target.value })} /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah (ml/L)</Label>
                    <Input type="number" step="0.1" value={form.probiotik_jumlah || ""} onChange={e => setForm({ ...form, probiotik_jumlah: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kondisi Air</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                    <Input placeholder="pH" type="number" step="0.1" value={form.kondisi_air.ph || ""} onChange={e => setForm({ ...form, kondisi_air: { ...form.kondisi_air, ph: parseFloat(e.target.value) || null } })} />
                    <Input placeholder="Suhu" type="number" step="0.1" value={form.kondisi_air.suhu || ""} onChange={e => setForm({ ...form, kondisi_air: { ...form.kondisi_air, suhu: parseFloat(e.target.value) || null } })} />
                    <Input placeholder="Salinitas" type="number" step="0.1" value={form.kondisi_air.salinitas || ""} onChange={e => setForm({ ...form, kondisi_air: { ...form.kondisi_air, salinitas: parseFloat(e.target.value) || null } })} />
                    <Input placeholder="DO" type="number" step="0.1" value={form.kondisi_air.do_level || ""} onChange={e => setForm({ ...form, kondisi_air: { ...form.kondisi_air, do_level: parseFloat(e.target.value) || null } })} />
                    <Input placeholder="Kecerahan" type="number" step="0.1" value={form.kondisi_air.kecerahan || ""} onChange={e => setForm({ ...form, kondisi_air: { ...form.kondisi_air, kecerahan: parseFloat(e.target.value) || null } })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Sampling</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="Berat (g)" type="number" step="0.01" value={form.sampling.berat_rata_rata || ""} onChange={e => setForm({ ...form, sampling: { ...form.sampling, berat_rata_rata: parseFloat(e.target.value) || null } })} />
                    <Input placeholder="SR (%)" type="number" step="0.1" value={form.sampling.sr_estimasi || ""} onChange={e => setForm({ ...form, sampling: { ...form.sampling, sr_estimasi: parseFloat(e.target.value) || null } })} />
                    <Input placeholder="Populasi est." type="number" value={form.sampling.populasi_estimasi || ""} onChange={e => setForm({ ...form, sampling: { ...form.sampling, populasi_estimasi: parseInt(e.target.value) || null } })} />
                  </div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Keterangan</Label>
                  <Textarea data-testid="input-keterangan" placeholder="Catatan..." value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Foto</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 hover:border-[#B8860B] hover:text-[#B8860B] cursor-pointer transition-colors">
                      <Camera size={18} />{form.foto_base64 ? "Terpilih" : "Pilih foto"}
                      <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                    </label>
                    {form.foto_base64 && <img src={form.foto_base64} alt="Preview" className="w-12 h-12 rounded-lg object-cover border" />}
                  </div>
                </div>
                <Button type="submit" data-testid="submit-laporan-btn" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan Laporan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-laporan" placeholder="Cari laporan..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
        </div>
        <Select value={filterSiklus} onValueChange={(v) => { setFilterSiklus(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-siklus" className="w-[260px] h-9"><SelectValue placeholder="Semua Siklus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Semua Siklus</SelectItem>
            {siklusList.map(s => <SelectItem key={s._id} value={s._id}>{s.tambak_nama} - {s.tanggal_tebar}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Tanggal</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Tambak</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">DOC</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Pakan (kg)</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">pH</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Pelapor</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Memuat...</TableCell></TableRow>
              ) : laporanList.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Belum ada laporan</TableCell></TableRow>
              ) : laporanList.map((r) => (
                <TableRow key={r._id} className="hover:bg-slate-50 transition-colors">
                  <TableCell className="text-sm">{r.tanggal}</TableCell>
                  <TableCell className="text-sm font-medium">{r.tambak_nama}</TableCell>
                  <TableCell className="text-sm">{r.hari_ke}</TableCell>
                  <TableCell className="text-sm">{r.pakan?.[0]?.jumlah_kg || "-"}</TableCell>
                  <TableCell className="text-sm">{r.kondisi_air?.ph || "-"}</TableCell>
                  <TableCell className="text-sm">{r.pelapor_nama}</TableCell>
                  <TableCell>
                    <Badge className={r.status === "verified" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>
                      {r.status === "verified" ? "Terverifikasi" : "Menunggu"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setDetailDialog(r)}><Eye size={16} /></Button>
                      {r.status !== "verified" && (
                        <Button variant="ghost" size="sm" onClick={() => handleVerify(r._id)} className="text-emerald-600"><CheckCircle size={16} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-3">
            <Pagination page={page} limit={15} total={total} onPageChange={setPage} />
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Detail Laporan</DialogTitle></DialogHeader>
          {detailDialog && (
            <div className="space-y-4 mt-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Tambak:</span> <span className="font-medium">{detailDialog.tambak_nama}</span></div>
                <div><span className="text-slate-500">Tanggal:</span> <span className="font-medium">{detailDialog.tanggal}</span></div>
                <div><span className="text-slate-500">DOC:</span> <span className="font-medium">Hari ke-{detailDialog.hari_ke}</span></div>
                <div><span className="text-slate-500">Kematian:</span> <span className="font-medium">{detailDialog.kematian}</span></div>
              </div>
              {detailDialog.pakan?.[0] && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Pakan</p>
                  <p>{detailDialog.pakan[0].jenis} - {detailDialog.pakan[0].jumlah_kg} kg ({detailDialog.pakan[0].frekuensi}x/hari)</p>
                </div>
              )}
              {detailDialog.kondisi_air && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Kondisi Air</p>
                  <div className="grid grid-cols-3 gap-2">
                    <span>pH: {detailDialog.kondisi_air.ph ?? "-"}</span>
                    <span>Suhu: {detailDialog.kondisi_air.suhu ?? "-"}&deg;C</span>
                    <span>Salinitas: {detailDialog.kondisi_air.salinitas ?? "-"}</span>
                    <span>DO: {detailDialog.kondisi_air.do_level ?? "-"}</span>
                    <span>Kecerahan: {detailDialog.kondisi_air.kecerahan ?? "-"}</span>
                  </div>
                </div>
              )}
              {detailDialog.keterangan && <p className="text-slate-600">{detailDialog.keterangan}</p>}
              {detailDialog.foto_base64 && <img src={detailDialog.foto_base64} alt="Bukti" className="w-full rounded-lg border" />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
